import React, { useState, useEffect, useRef } from 'react'
import apiClient, { API_ENDPOINTS } from '../../config/api'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import html2canvas from 'html2canvas'
import Swal from 'sweetalert2'

const formatNumber = (value) => {
  const num = Number(value ?? 0)
  if (isNaN(num)) return '0.00'
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
}

const formatCurrency = (value) => `$${formatNumber(value)}`

const formatPercentage = (value) => {
  const num = Number(value ?? 0)
  if (isNaN(num)) return '0.00'
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
}

const formatDate = (dateString) => {
  if (!dateString) return 'N/A'
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const Reports = () => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)
  const [returnsRate, setReturnsRate] = useState(null)

  // Chart refs
  const activityTypeChartRef = useRef(null)
  const userActivityChartRef = useRef(null)
  const dailySalesTrendChartRef = useRef(null)
  const weeklySalesTrendChartRef = useRef(null)
  const salesByPaymentMethodChartRef = useRef(null)
  const salesByBranchChartRef = useRef(null)
  const topSellingProductsChartRef = useRef(null)
  const stockAdjustmentsChartRef = useRef(null)
  const reportsSectionRef = useRef(null)

  useEffect(() => {
    const fetchReports = async () => {
      setLoading(true)
      setError(null)
      try {
        const [auditorRes, returnsRateRes] = await Promise.all([
          apiClient.get(API_ENDPOINTS.AUDITOR_REPORTS),
          apiClient.get(API_ENDPOINTS.SALES_RETURNS_RATE).catch(err => {
            // If returns rate API fails, log but don't fail the whole report
            console.warn('Error fetching returns rate:', err)
            return { data: null }
          })
        ])
        setData(auditorRes.data)
        setReturnsRate(returnsRateRes.data)
      } catch (err) {
        console.error('Error fetching reports:', err)
        setError('Failed to load reports. Please try again later.')
      } finally {
        setLoading(false)
      }
    }

    fetchReports()
  }, [])

  // Initialize charts when data is available
  useEffect(() => {
    if (!data || !window.ApexCharts) return

    const destroyCharts = () => {
      const refs = [
        activityTypeChartRef, userActivityChartRef, dailySalesTrendChartRef,
        weeklySalesTrendChartRef, salesByPaymentMethodChartRef, salesByBranchChartRef,
        topSellingProductsChartRef, stockAdjustmentsChartRef
      ]
      refs.forEach(ref => {
        if (ref.current?.chart) {
          ref.current.chart.destroy()
          ref.current.chart = null
        }
      })
    }

    destroyCharts()

    // Activity Type Summary - Donut Chart
    if (data.audit_summary?.activity_type_summary?.length && activityTypeChartRef.current) {
      const chart = new window.ApexCharts(activityTypeChartRef.current, {
        series: data.audit_summary.activity_type_summary.map(item => item.count),
        chart: {
          type: 'donut',
          height: 350,
          toolbar: { show: true }
        },
        labels: data.audit_summary.activity_type_summary.map(item => item.activity_type),
        tooltip: {
          y: {
            formatter: (val) => `${val} activities`
          }
        },
        colors: ['#4154f1', '#2eca6a', '#ff771d', '#f72585', '#7209b7', '#06ffa5', '#ff006e']
      })
      chart.render()
      activityTypeChartRef.current.chart = chart
    }

    // User Activity - Bar Chart
    if (data.audit_summary?.user_activity?.length && userActivityChartRef.current) {
      const chart = new window.ApexCharts(userActivityChartRef.current, {
        series: [{
          name: 'Activity Count',
          data: data.audit_summary.user_activity.map(item => item.activity_count)
        }],
        chart: {
          type: 'bar',
          height: 350,
          toolbar: { show: true }
        },
        plotOptions: {
          bar: {
            horizontal: true,
            borderRadius: 4
          }
        },
        xaxis: {
          categories: data.audit_summary.user_activity.map(item => item.user_email)
        },
        colors: ['#4154f1']
      })
      chart.render()
      userActivityChartRef.current.chart = chart
    }

    // Daily Sales Trend - Line Chart
    if (data.sales_trends?.daily_sales_trend?.length && dailySalesTrendChartRef.current) {
      const chart = new window.ApexCharts(dailySalesTrendChartRef.current, {
        series: [
          {
            name: 'Revenue',
            data: data.sales_trends.daily_sales_trend.map(item => Number(item.total_revenue || 0))
          },
          {
            name: 'Items Sold',
            data: data.sales_trends.daily_sales_trend.map(item => Number(item.total_items_sold || 0))
          }
        ],
        chart: {
          type: 'line',
          height: 350,
          toolbar: { show: true }
        },
        xaxis: {
          categories: data.sales_trends.daily_sales_trend.map(item => {
            const date = new Date(item.day)
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
          })
        },
        yaxis: [
          {
            title: { text: 'Revenue ($)' },
            labels: { formatter: (val) => formatCurrency(val) }
          },
          {
            opposite: true,
            title: { text: 'Items Sold' },
            labels: { formatter: (val) => Math.round(val) }
          }
        ],
        colors: ['#4154f1', '#2eca6a']
      })
      chart.render()
      dailySalesTrendChartRef.current.chart = chart
    }

    // Weekly Sales Trend - Column Chart
    if (data.sales_trends?.weekly_sales_trend?.length && weeklySalesTrendChartRef.current) {
      const chart = new window.ApexCharts(weeklySalesTrendChartRef.current, {
        series: [{
          name: 'Revenue',
          data: data.sales_trends.weekly_sales_trend.map(item => Number(item.total_revenue || 0))
        }],
        chart: {
          type: 'bar',
          height: 350,
          toolbar: { show: true }
        },
        xaxis: {
          categories: data.sales_trends.weekly_sales_trend.map(item => {
            const date = new Date(item.week)
            return `Week of ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
          })
        },
        yaxis: {
          labels: { formatter: (val) => formatCurrency(val) }
        },
        colors: ['#2eca6a']
      })
      chart.render()
      weeklySalesTrendChartRef.current.chart = chart
    }

    // Sales by Payment Method - Pie Chart
    if (data.sales_trends?.sales_by_payment_method?.length && salesByPaymentMethodChartRef.current) {
      const chart = new window.ApexCharts(salesByPaymentMethodChartRef.current, {
        series: data.sales_trends.sales_by_payment_method.map(item => item.total_revenue),
        chart: {
          type: 'pie',
          height: 350,
          toolbar: { show: true }
        },
        labels: data.sales_trends.sales_by_payment_method.map(item => item.payment_method),
        tooltip: {
          y: {
            formatter: (val) => formatCurrency(val)
          }
        },
        colors: ['#4154f1', '#2eca6a', '#ff771d']
      })
      chart.render()
      salesByPaymentMethodChartRef.current.chart = chart
    }

    // Sales by Branch - Donut Chart
    if (data.sales_trends?.sales_by_branch?.length && salesByBranchChartRef.current) {
      const chart = new window.ApexCharts(salesByBranchChartRef.current, {
        series: data.sales_trends.sales_by_branch.map(item => item.total_revenue),
        chart: {
          type: 'donut',
          height: 350,
          toolbar: { show: true }
        },
        labels: data.sales_trends.sales_by_branch.map(item => item.branch_name),
        tooltip: {
          y: {
            formatter: (val) => formatCurrency(val)
          }
        },
        colors: ['#4154f1', '#2eca6a', '#ff771d']
      })
      chart.render()
      salesByBranchChartRef.current.chart = chart
    }

    // Top Selling Products - Bar Chart
    if (data.sales_trends?.top_selling_products?.length && topSellingProductsChartRef.current) {
      const chart = new window.ApexCharts(topSellingProductsChartRef.current, {
        series: [{
          name: 'Revenue',
          data: data.sales_trends.top_selling_products.map(item => Number(item.total_revenue || 0))
        }],
        chart: {
          type: 'bar',
          height: 350,
          toolbar: { show: true }
        },
        plotOptions: {
          bar: {
            horizontal: true,
            borderRadius: 4
          }
        },
        xaxis: {
          categories: data.sales_trends.top_selling_products.map(item => item.product_name)
        },
        yaxis: {
          labels: { formatter: (val) => formatCurrency(val) }
        },
        colors: ['#ff771d']
      })
      chart.render()
      topSellingProductsChartRef.current.chart = chart
    }

    // Stock Adjustments by Type - Pie Chart
    if (data.stock_movement?.adjustments_by_type?.length && stockAdjustmentsChartRef.current) {
      const chart = new window.ApexCharts(stockAdjustmentsChartRef.current, {
        series: data.stock_movement.adjustments_by_type.map(item => item.count),
        chart: {
          type: 'pie',
          height: 350,
          toolbar: { show: true }
        },
        labels: data.stock_movement.adjustments_by_type.map(item => item.adjustment_type),
        tooltip: {
          y: {
            formatter: (val) => `${val} adjustments`
          }
        },
        colors: ['#2eca6a', '#dc3545', '#ffc107']
      })
      chart.render()
      stockAdjustmentsChartRef.current.chart = chart
    }

    return () => {
      destroyCharts()
    }
  }, [data])

  const generatePDF = async () => {
    if (!data) {
      Swal.fire({
        icon: 'warning',
        title: 'No Data',
        text: 'No report data available to export.',
        confirmButtonColor: '#3085d6'
      })
      return
    }

    if (!reportsSectionRef.current) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Unable to capture report section.',
        confirmButtonColor: '#3085d6'
      })
      return
    }

    try {
      Swal.fire({
        title: 'Generating PDF...',
        text: 'Please wait while we capture the report.',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading()
        }
      })

      const canvas = await html2canvas(reportsSectionRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: reportsSectionRef.current.scrollWidth,
        windowHeight: reportsSectionRef.current.scrollHeight
      })

      const imgData = canvas.toDataURL('image/png')
      const imgWidth = 210
      const pageHeight = 297
      const imgHeight = (canvas.height * imgWidth) / canvas.width
      let heightLeft = imgHeight

      const doc = new jsPDF('portrait', 'mm', 'a4')
      let position = 0

      doc.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight)
      heightLeft -= pageHeight

      while (heightLeft > 0) {
        position = heightLeft - imgHeight
        doc.addPage()
        doc.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight)
        heightLeft -= pageHeight
      }

      const fileName = `Auditor_Reports_${new Date().toISOString().split('T')[0]}.pdf`
      doc.save(fileName)

      Swal.fire({
        icon: 'success',
        title: 'PDF Generated',
        text: 'The report has been downloaded successfully.',
        confirmButtonColor: '#3085d6',
        timer: 2000
      })
    } catch (error) {
      console.error('Error generating PDF:', error)
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to generate PDF. Please try again.',
        confirmButtonColor: '#3085d6'
      })
    }
  }

  if (loading) {
    return (
      <>
        <div className="pagetitle">
          <h1>Auditor Reports</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item"><a href="/">Home</a></li>
              <li className="breadcrumb-item active">Reports</li>
            </ol>
          </nav>
        </div>
        <section className="section">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading reports...</span>
            </div>
          </div>
        </section>
      </>
    )
  }

  if (error) {
    return (
      <>
        <div className="pagetitle">
          <h1>Auditor Reports</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item"><a href="/">Home</a></li>
              <li className="breadcrumb-item active">Reports</li>
            </ol>
          </nav>
        </div>
        <section className="section">
          <div className="alert alert-danger d-flex align-items-center" role="alert">
            <i className="bi bi-exclamation-triangle me-2"></i>
            {error}
          </div>
        </section>
      </>
    )
  }

  return (
    <>
      <div className="pagetitle">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h1>Auditor Reports</h1>
            {data?.date_range && (
              <p className="text-muted mb-0">
                <i className="bi bi-calendar-range me-2"></i>
                {formatDate(data.date_range.start_date)} - {formatDate(data.date_range.end_date)}
              </p>
            )}
            <nav>
              <ol className="breadcrumb">
                <li className="breadcrumb-item"><a href="/">Home</a></li>
                <li className="breadcrumb-item active">Reports</li>
              </ol>
            </nav>
          </div>
          {data && (
            <button
              onClick={generatePDF}
              className="btn btn-primary"
              style={{ height: 'fit-content' }}
            >
              <i className="bi bi-file-earmark-pdf me-2"></i>
              Download PDF
            </button>
          )}
        </div>
      </div>

      <section className="section" ref={reportsSectionRef}>
        {/* Audit Summary Section */}
        <div className="row mb-4">
          <div className="col-12">
            <h5 className="card-title mb-3">
              <i className="bi bi-shield-check text-primary me-2"></i>
              Audit Summary
            </h5>
          </div>
        </div>

        <div className="row g-4 mb-4">
          <div className="col-lg-3 col-md-6">
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <h6 className="text-muted mb-2">Total Audit Logs</h6>
                <h3 className="mb-0">{data?.audit_summary?.total_logs || 0}</h3>
              </div>
            </div>
          </div>
          <div className="col-lg-3 col-md-6">
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <h6 className="text-muted mb-2">Total Sales</h6>
                <h3 className="mb-0">{data?.sales_summary?.total_sales || 0}</h3>
              </div>
            </div>
          </div>
          <div className="col-lg-3 col-md-6">
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <h6 className="text-muted mb-2">Total Revenue</h6>
                <h3 className="mb-0">{formatCurrency(data?.sales_summary?.total_revenue || 0)}</h3>
              </div>
            </div>
          </div>
          <div className="col-lg-3 col-md-6">
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <h6 className="text-muted mb-2">Total Items Sold</h6>
                <h3 className="mb-0">{formatNumber(data?.sales_summary?.total_items_sold || 0)}</h3>
              </div>
            </div>
          </div>
        </div>

        {/* Activity Type and User Activity Charts */}
        <div className="row g-4 mb-4">
          <div className="col-lg-6">
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <h5 className="card-title">Activity Type Distribution</h5>
                <div ref={activityTypeChartRef}></div>
              </div>
            </div>
          </div>
          <div className="col-lg-6">
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <h5 className="card-title">User Activity</h5>
                <div ref={userActivityChartRef}></div>
              </div>
            </div>
          </div>
        </div>

        {/* User Activity Table */}
        {data?.audit_summary?.user_activity?.length > 0 && (
          <div className="row mb-4">
            <div className="col-12">
              <div className="card shadow-sm border-0">
                <div className="card-body">
                  <h5 className="card-title">User Activity Summary</h5>
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>User Email</th>
                          <th>Activity Count</th>
                          <th>Last Activity</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.audit_summary.user_activity.map((user, idx) => (
                          <tr key={idx}>
                            <td>{user.user_email}</td>
                            <td>{user.activity_count}</td>
                            <td>{formatDate(user.last_activity)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Sales Summary Section */}
        <div className="row mb-4">
          <div className="col-12">
            <h5 className="card-title mb-3">
              <i className="bi bi-cart-check text-success me-2"></i>
              Sales Summary
            </h5>
          </div>
        </div>

        <div className="row g-4 mb-4">
          <div className="col-lg-3 col-md-6">
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <h6 className="text-muted mb-2">Completed Sales</h6>
                <h3 className="mb-0 text-success">{data?.sales_summary?.completed_sales || 0}</h3>
              </div>
            </div>
          </div>
          <div className="col-lg-3 col-md-6">
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <h6 className="text-muted mb-2">Pending Sales</h6>
                <h3 className="mb-0 text-warning">{data?.sales_summary?.pending_sales || 0}</h3>
              </div>
            </div>
          </div>
          <div className="col-lg-3 col-md-6">
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <h6 className="text-muted mb-2">Cancelled Sales</h6>
                <h3 className="mb-0 text-danger">{data?.sales_summary?.cancelled_sales || 0}</h3>
              </div>
            </div>
          </div>
          <div className="col-lg-3 col-md-6">
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <h6 className="text-muted mb-2">Total Discount</h6>
                <h3 className="mb-0">{formatCurrency(data?.sales_summary?.total_discount || 0)}</h3>
              </div>
            </div>
          </div>
        </div>

        {/* Payments Summary */}
        {data?.payments_summary?.length > 0 && (
          <div className="row mb-4">
            <div className="col-12">
              <div className="card shadow-sm border-0">
                <div className="card-body">
                  <h5 className="card-title">Payments Summary</h5>
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>Payment Method</th>
                          <th>Transaction Count</th>
                          <th className="text-end">Total Revenue</th>
                          <th className="text-end">Average Transaction</th>
                          <th className="text-end">Percentage of Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.payments_summary.map((payment, idx) => (
                          <tr key={idx}>
                            <td><span className="badge bg-primary">{payment.payment_method}</span></td>
                            <td>{payment.transaction_count}</td>
                            <td className="text-end fw-semibold">{formatCurrency(payment.total_revenue)}</td>
                            <td className="text-end">{formatCurrency(payment.average_transaction)}</td>
                            <td className="text-end">{formatNumber(payment.percentage_of_total)}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Sales Trends Section */}
        <div className="row mb-4">
          <div className="col-12">
            <h5 className="card-title mb-3">
              <i className="bi bi-graph-up-arrow text-info me-2"></i>
              Sales Trends
            </h5>
          </div>
        </div>

        <div className="row g-4 mb-4">
          <div className="col-lg-6">
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <h5 className="card-title">Daily Sales Trend</h5>
                <div ref={dailySalesTrendChartRef}></div>
              </div>
            </div>
          </div>
          <div className="col-lg-6">
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <h5 className="card-title">Weekly Sales Trend</h5>
                <div ref={weeklySalesTrendChartRef}></div>
              </div>
            </div>
          </div>
        </div>

        <div className="row g-4 mb-4">
          <div className="col-lg-4">
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <h5 className="card-title">Sales by Payment Method</h5>
                <div ref={salesByPaymentMethodChartRef}></div>
              </div>
            </div>
          </div>
          <div className="col-lg-4">
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <h5 className="card-title">Sales by Branch</h5>
                <div ref={salesByBranchChartRef}></div>
              </div>
            </div>
          </div>
          <div className="col-lg-4">
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <h5 className="card-title">Top Selling Products</h5>
                <div ref={topSellingProductsChartRef}></div>
              </div>
            </div>
          </div>
        </div>

        {/* Stock Report Section */}
        {data?.stock_report && (
          <div className="row mb-4">
            <div className="col-12">
              <h5 className="card-title mb-3">
                <i className="bi bi-box-seam text-warning me-2"></i>
                Stock Report
              </h5>
            </div>
          </div>
        )}

        {data?.stock_report?.warehouse_summary && (
          <div className="row g-4 mb-4">
            <div className="col-lg-4 col-md-6">
              <div className="card shadow-sm border-0">
                <div className="card-body">
                  <h6 className="text-muted mb-2">Total Products (Warehouse)</h6>
                  <h3 className="mb-0">{data.stock_report.warehouse_summary.total_products || 0}</h3>
                </div>
              </div>
            </div>
            <div className="col-lg-4 col-md-6">
              <div className="card shadow-sm border-0">
                <div className="card-body">
                  <h6 className="text-muted mb-2">Total Quantity (Warehouse)</h6>
                  <h3 className="mb-0">{formatNumber(data.stock_report.warehouse_summary.total_quantity || 0)}</h3>
                </div>
              </div>
            </div>
            <div className="col-lg-4 col-md-6">
              <div className="card shadow-sm border-0">
                <div className="card-body">
                  <h6 className="text-muted mb-2">Total Cost (Warehouse)</h6>
                  <h3 className="mb-0">{formatCurrency(data.stock_report.warehouse_summary.total_cost || 0)}</h3>
                </div>
              </div>
            </div>
          </div>
        )}

        {data?.stock_report?.branch_summary && (
          <div className="row g-4 mb-4">
            <div className="col-lg-3 col-md-6">
              <div className="card shadow-sm border-0">
                <div className="card-body">
                  <h6 className="text-muted mb-2">Total Products (Branch)</h6>
                  <h3 className="mb-0">{data.stock_report.branch_summary.total_products || 0}</h3>
                </div>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="card shadow-sm border-0">
                <div className="card-body">
                  <h6 className="text-muted mb-2">Total Quantity (Branch)</h6>
                  <h3 className="mb-0">{formatNumber(data.stock_report.branch_summary.total_quantity || 0)}</h3>
                </div>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="card shadow-sm border-0">
                <div className="card-body">
                  <h6 className="text-muted mb-2">Low Stock Count</h6>
                  <h3 className="mb-0 text-danger">{data.stock_report.branch_summary.low_stock_count || 0}</h3>
                </div>
              </div>
            </div>
            <div className="col-lg-3 col-md-6">
              <div className="card shadow-sm border-0">
                <div className="card-body">
                  <h6 className="text-muted mb-2">Total Selling Value</h6>
                  <h3 className="mb-0">{formatCurrency(data.stock_report.branch_summary.total_selling_value || 0)}</h3>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stock Movement Section */}
        {data?.stock_movement && (
          <div className="row mb-4">
            <div className="col-12">
              <h5 className="card-title mb-3">
                <i className="bi bi-arrow-left-right text-secondary me-2"></i>
                Stock Movement
              </h5>
            </div>
          </div>
        )}

        <div className="row g-4 mb-4">
          <div className="col-lg-6">
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <h5 className="card-title">Stock Adjustments by Type</h5>
                <div ref={stockAdjustmentsChartRef}></div>
              </div>
            </div>
          </div>
          <div className="col-lg-6">
            {data?.stock_movement?.transfers_by_type?.length > 0 && (
              <div className="card shadow-sm border-0">
                <div className="card-body">
                  <h5 className="card-title">Transfers by Type</h5>
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>Transfer Type</th>
                          <th>Status</th>
                          <th>Count</th>
                          <th className="text-end">Total Quantity</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.stock_movement.transfers_by_type.map((transfer, idx) => (
                          <tr key={idx}>
                            <td>{transfer.transfer_type}</td>
                            <td>
                              <span className={`badge ${
                                transfer.status === 'completed' ? 'bg-success' : 
                                transfer.status === 'pending' ? 'bg-warning' : 'bg-secondary'
                              }`}>
                                {transfer.status}
                              </span>
                            </td>
                            <td>{transfer.count}</td>
                            <td className="text-end">{formatNumber(transfer.total_quantity)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Returns Rate Section */}
        {returnsRate && (
          <>
            <div className="row">
              <div className="col-12">
                <h5 className="card-title mb-3">
                  <i className="bi bi-arrow-counterclockwise text-primary me-2"></i>
                  Returns Rate Analysis
                </h5>
              </div>
            </div>

            {/* Returns Rate Summary Cards */}
            <div className="row g-3 mb-4">
              <div className="col-md-3 col-6">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <h6 className="text-muted mb-2">Total Sold Quantity</h6>
                    <h4 className="mb-0 text-primary">{formatNumber(returnsRate.metrics?.total_sold_quantity || 0)}</h4>
                  </div>
                </div>
              </div>
              <div className="col-md-3 col-6">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <h6 className="text-muted mb-2">Total Sales Value</h6>
                    <h4 className="mb-0 text-success">{formatCurrency(returnsRate.metrics?.total_sales_value || 0)}</h4>
                  </div>
                </div>
              </div>
              <div className="col-md-3 col-6">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <h6 className="text-muted mb-2">Total Returned Quantity</h6>
                    <h4 className="mb-0 text-danger">{formatNumber(returnsRate.metrics?.total_returned_quantity || 0)}</h4>
                  </div>
                </div>
              </div>
              <div className="col-md-3 col-6">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <h6 className="text-muted mb-2">Total Refund Amount</h6>
                    <h4 className="mb-0 text-warning">{formatCurrency(returnsRate.metrics?.total_refund_amount || 0)}</h4>
                  </div>
                </div>
              </div>
            </div>

            {/* Return Rate Metrics */}
            <div className="row g-3 mb-4">
              <div className="col-md-6">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <h6 className="text-muted mb-2">Quantity Return Rate</h6>
                    <h4 className="mb-0 text-info">{formatPercentage(returnsRate.metrics?.quantity_return_rate || 0)}%</h4>
                    <small className="text-muted">
                      {returnsRate.metrics?.total_returned_quantity || 0} of {returnsRate.metrics?.total_sold_quantity || 0} items returned
                    </small>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <h6 className="text-muted mb-2">Value Return Rate</h6>
                    <h4 className="mb-0 text-info">{formatPercentage(returnsRate.metrics?.value_return_rate || 0)}%</h4>
                    <small className="text-muted">
                      {formatCurrency(returnsRate.metrics?.total_refund_amount || 0)} of {formatCurrency(returnsRate.metrics?.total_sales_value || 0)} refunded
                    </small>
                  </div>
                </div>
              </div>
            </div>

            {/* Top Returned Products */}
            {returnsRate.top_returned_products?.length > 0 && (
              <div className="row g-4 mb-4">
                <div className="col-12">
                  <div className="card shadow-sm border-0">
                    <div className="card-body">
                      <h5 className="card-title">Top Returned Products</h5>
                      <div className="mt-3">
                        <div className="table-responsive">
                          <table className="table table-hover">
                            <thead className="table-light">
                              <tr>
                                <th>Product</th>
                                <th>SKU</th>
                                <th className="text-end">Returned Quantity</th>
                                <th className="text-end">Refund Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {returnsRate.top_returned_products.map((product, idx) => (
                                <tr key={product.product_id || idx}>
                                  <td className="fw-semibold">{product.product_name}</td>
                                  <td><span className="badge bg-secondary">{product.product_sku}</span></td>
                                  <td className="text-end text-danger fw-semibold">{formatNumber(product.total_returned_quantity)}</td>
                                  <td className="text-end text-warning fw-semibold">{formatCurrency(product.total_refund_amount)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Filter Information */}
            {returnsRate.filters && (
              <div className="row mb-4">
                <div className="col-12">
                  <div className="card border-0 shadow-sm">
                    <div className="card-body">
                      <h6 className="text-muted mb-2">Report Period</h6>
                      <p className="mb-0">
                        <strong>Start Date:</strong> {returnsRate.filters.start_date ? new Date(returnsRate.filters.start_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'} &nbsp;|&nbsp;
                        <strong>End Date:</strong> {returnsRate.filters.end_date ? new Date(returnsRate.filters.end_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'}
                        {returnsRate.filters.branch_id && (
                          <> &nbsp;|&nbsp; <strong>Branch ID:</strong> {returnsRate.filters.branch_id}</>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* Audit Logs Table */}
        {data?.audit_logs?.length > 0 && (
          <div className="row mb-4">
            <div className="col-12">
              <div className="card shadow-sm border-0">
                <div className="card-body">
                  <h5 className="card-title">Recent Audit Logs</h5>
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>Timestamp</th>
                          <th>Activity Type</th>
                          <th>User Email</th>
                          <th>Description</th>
                          <th>IP Address</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.audit_logs.slice(0, 50).map((log) => (
                          <tr key={log.id}>
                            <td>{formatDate(log.timestamp)}</td>
                            <td>
                              <span className="badge bg-info">{log.activity_type}</span>
                            </td>
                            <td>{log.user_email}</td>
                            <td>{log.description}</td>
                            <td>{log.ip_address}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {data.audit_logs.length > 50 && (
                    <div className="text-muted text-center mt-2">
                      Showing first 50 of {data.audit_logs.length} audit logs
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Sales Audit Report Table */}
        {data?.sales_audit_report?.sales_audit_report?.length > 0 && (
          <div className="row mb-4">
            <div className="col-12">
              <div className="card shadow-sm border-0">
                <div className="card-body">
                  <h5 className="card-title">Sales Audit Report</h5>
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>Sale Number</th>
                          <th>Branch</th>
                          <th>Cashier</th>
                          <th>Status</th>
                          <th className="text-end">Total Amount</th>
                          <th>Payment Type</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.sales_audit_report.sales_audit_report.slice(0, 50).map((sale) => (
                          <tr key={sale.sale_id}>
                            <td>{sale.sale_number}</td>
                            <td>{sale.branch_name}</td>
                            <td>{sale.cashier_email}</td>
                            <td>
                              <span className={`badge ${
                                sale.status === 'completed' ? 'bg-success' : 
                                sale.status === 'pending' ? 'bg-warning' : 
                                sale.status === 'cancelled' ? 'bg-danger' : 'bg-secondary'
                              }`}>
                                {sale.status}
                              </span>
                            </td>
                            <td className="text-end fw-semibold">{formatCurrency(sale.total_amount)}</td>
                            <td>{sale.type_of_payment}</td>
                            <td>{formatDate(sale.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {data.sales_audit_report.sales_audit_report.length > 50 && (
                    <div className="text-muted text-center mt-2">
                      Showing first 50 of {data.sales_audit_report.sales_audit_report.length} sales
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </section>
    </>
  )
}

export default Reports
